import { Box, CircularProgress, Divider } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";

import { ProfileSettings } from "./components/ProfileSettings";
import { AccessSettings } from "./components/AccessSettings";
import { ContractSettings } from "./components/ContractSettings";
import { VerticalTabs } from "./components/VerticalTabs";
import { Action } from "./components/Action";
import useEditOrganization from "./hooks/useEditOrganization";
import newOrganizationResolver from "../../../../libs/form-validations/organization";
import Breadcrumb from "./components/Breadcrumb";

import { Alert } from "../../../../shared";
import { useAuthorization } from "../../../../hooks";
import { ROLES } from "../../../../constants/permissions";

const INITIAL_VALUES = {
    Name: "",
    ShortName: "",
    Description: "",
    Website: "",
    LogoUrl: "",
    LogoFile: null,
    IsActive: true,
    ContractTemplateId: "standard",
};

export default function Edit() {
    const { hasRole } = useAuthorization();
    const isSuperAdmin = hasRole(ROLES.SUPER_ADMIN);
    const [activeTab, setActiveTab] = useState("profile");
    const {
        handleEditOrganization,
        editOrganizationSvc,
        organizationData,
        contractTemplates,
        organizationContractTemplate,
        isLoading,
    } = useEditOrganization();

    const methods = useForm({
        resolver: newOrganizationResolver,
        defaultValues: INITIAL_VALUES,
        shouldUnregister: false,
    });

    useEffect(() => {
        if (organizationData) {
            methods.reset({
                Name: organizationData.Name,
                ShortName: organizationData.ShortName || "",
                Description: organizationData.Description || "",
                Website: organizationData.Website || "",
                LogoUrl: organizationData.LogoUrl || "",
                LogoFile: null,
                IsActive: organizationData.IsActive === true || organizationData.IsActive === 1,
                ContractTemplateId:
                    organizationContractTemplate?.ContractTemplateId || "standard",
            });
        }
    }, [organizationData, organizationContractTemplate, methods]);

    if (!isSuperAdmin) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Only Super Admin can edit organizations." />
            </Box>
        );
    }

    const onSubmit = methods.handleSubmit(handleEditOrganization);
    const isActive = organizationData?.IsActive === true || organizationData?.IsActive === 1;

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0 }}
        >
            <Breadcrumb organizationName={organizationData?.Name} />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <FormProvider {...methods}>
                <Box
                    component="form"
                    onSubmit={onSubmit}
                    width="100%"
                    height="100%"
                    display="flex"
                    flex={1}
                    sx={{ overflow: "hidden", minWidth: 0 }}
                >
                    <VerticalTabs value={activeTab} onChange={setActiveTab} />
                    <Box
                        flex={1}
                        sx={{
                            overflow: "auto",
                            minWidth: 0,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {activeTab === "profile" && <ProfileSettings />}
                        {activeTab === "access" && (
                            <AccessSettings
                                organizationId={organizationData?.Id}
                                isActive={isActive}
                            />
                        )}
                        {activeTab === "contract" && (
                            <ContractSettings contractTemplates={contractTemplates} />
                        )}

                        <Action api={editOrganizationSvc} />
                        <Alert
                            successMessage={
                                editOrganizationSvc.isSuccess &&
                                "Organization updated successfully!"
                            }
                            errorMessage={
                                editOrganizationSvc.isError &&
                                "Failed to update organization. Please try again."
                            }
                        />
                    </Box>
                </Box>
            </FormProvider>
        </Box>
    );
}
